import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Button, Table, InputNumber, Switch, message, Space, Tooltip } from 'antd';
import { PlusOutlined, DeleteOutlined, InfoCircleOutlined } from '@ant-design/icons';

const { Option } = Select;
const { TextArea } = Input;

const SalaryTemplateForm = ({ 
  visible, 
  onCancel, 
  onSubmit, 
  initialData = null, 
  positions = [], 
  salaryItems = [] 
}) => {
  const [form] = Form.useForm();
  const [templateItems, setTemplateItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible) {
      if (initialData) {
        // 编辑模式
        form.setFieldsValue({
          position_id: initialData.position_id,
          name: initialData.name,
          description: initialData.description,
          effective_date: initialData.effective_date,
          is_active: initialData.is_active
        });
        // 转换模板项目数据格式
        const items = (initialData.items || []).map(item => ({
          id: item.id || Date.now(),
          salary_item_name: item.salary_item_name,
          salary_item_type: item.salary_item_type,
          calculation_type: item.calculation_type,
          value: item.value || item.amount || item.percentage || 0, // 统一使用value字段
          description: item.description || '', // 添加说明字段
          formula: item.formula,
          min_amount: item.min_amount,
          max_amount: item.max_amount,
          is_required: item.is_required,
          sort_order: item.sort_order,
          performance_related: item.performance_related || false,
          target_amount: item.target_amount || 0,
          department_id: item.department_id || null,
          store_id: item.store_id || null,
          cycle_type: item.cycle_type || 'monthly'
        }));
        setTemplateItems(items);
      } else {
        // 新建模式
        form.resetFields();
        setTemplateItems([]);
      }
    }
  }, [visible, initialData, form]);

  const handleAddItem = () => {
    const newItem = {
      id: Date.now(),
      salary_item_name: '',
      salary_item_type: 'base',
      calculation_type: 'fixed',
      value: 0, // 统一使用value字段
      description: '', // 添加说明字段
      formula: '',
      min_amount: null,
      max_amount: null,
      is_required: false,
      sort_order: templateItems.length,
      performance_related: false,
      target_amount: 0,
      department_id: null,
      store_id: null,
      cycle_type: 'monthly'
    };
    setTemplateItems([...templateItems, newItem]);
  };

  const handleRemoveItem = (index) => {
    const newItems = templateItems.filter((_, i) => i !== index);
    setTemplateItems(newItems);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...templateItems];
    newItems[index] = { ...newItems[index], [field]: value };
    setTemplateItems(newItems);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      
      const submitData = {
        ...values,
        items: templateItems.filter(item => item.salary_item_name && item.salary_item_name.trim())
      };
      
      await onSubmit(submitData);
      message.success(initialData ? '薪资模板更新成功' : '薪资模板创建成功');
      onCancel();
    } catch (error) {
      console.error('提交失败:', error);
    } finally {
      setLoading(false);
    }
  };


  const columns = [
    {
      title: '薪资项目名称',
      key: 'salary_item_name',
      width: 180,
      render: (_, record, index) => (
        <Input
          value={record.salary_item_name}
          onChange={(e) => handleItemChange(index, 'salary_item_name', e.target.value)}
          placeholder="输入薪资项目名称"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '类型',
      key: 'salary_item_type',
      width: 120,
      render: (_, record, index) => (
        <Select
          value={record.salary_item_type}
          onChange={(value) => handleItemChange(index, 'salary_item_type', value)}
          style={{ width: '100%' }}
        >
          <Option value="base">基本工资</Option>
          <Option value="allowance">津贴</Option>
          <Option value="bonus">奖金</Option>
          <Option value="deduction">扣除</Option>
        </Select>
      ),
    },
    {
      title: '计算方式',
      key: 'calculation_type',
      width: 120,
      render: (_, record, index) => (
        <Select
          value={record.calculation_type}
          onChange={(value) => handleItemChange(index, 'calculation_type', value)}
          style={{ width: '100%' }}
        >
          <Option value="fixed">固定金额</Option>
          <Option value="percentage">百分比</Option>
          <Option value="formula">公式计算</Option>
        </Select>
      ),
    },
    {
      title: () => (
        <span>
          值 
          <Tooltip title="固定金额、百分比值或公式值">
            <InfoCircleOutlined style={{ marginLeft: 4, color: '#999' }} />
          </Tooltip>
        </span>
      ),
      key: 'value',
      width: 120,
      render: (_, record, index) => {
        if (record.calculation_type === 'percentage') {
          return (
            <InputNumber
              value={parseFloat(record.value) || parseFloat(record.percentage) || 0}
              onChange={(value) => handleItemChange(index, 'value', value || 0)}
              placeholder="0"
              min={0}
              max={100}
              precision={2}
              style={{ width: '100%' }}
              formatter={value => `${value}%`}
              parser={value => value.replace('%', '')}
            />
          );
        } else if (record.calculation_type === 'formula') {
          return (
            <Input
              value={record.formula}
              onChange={(e) => handleItemChange(index, 'formula', e.target.value)}
              placeholder="输入计算公式"
              style={{ width: '100%' }}
            />
          );
        } else {
          return (
            <InputNumber
              value={record.value}
              onChange={(value) => handleItemChange(index, 'value', value || 0)}
              placeholder="0"
              min={0}
              precision={2}
              style={{ width: '100%' }}
              formatter={value => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/¥\s?|(,*)/g, '')}
            />
          );
        }
      },
    },
    {
      title: () => (
        <span>
          说明 
          <Tooltip title="薪资项目的详细说明">
            <InfoCircleOutlined style={{ marginLeft: 4, color: '#999' }} />
          </Tooltip>
        </span>
      ),
      key: 'description',
      width: 150,
      render: (_, record, index) => (
        <Input
          value={record.description}
          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
          placeholder="项目说明"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: () => (
        <span>
          必需项 
          <Tooltip title="是否为必需的薪资项目">
            <InfoCircleOutlined style={{ marginLeft: 4, color: '#999' }} />
          </Tooltip>
        </span>
      ),
      key: 'is_required',
      width: 80,
      render: (_, record, index) => (
        <Switch
          checked={record.is_required}
          onChange={(checked) => handleItemChange(index, 'is_required', checked)}
          size="small"
        />
      ),
    },
    {
      title: () => (
        <span>
          业绩相关 
          <Tooltip title="是否为业绩相关项目">
            <InfoCircleOutlined style={{ marginLeft: 4, color: '#999' }} />
          </Tooltip>
        </span>
      ),
      key: 'performance_related',
      width: 90,
      render: (_, record, index) => (
        <Switch
          checked={record.performance_related}
          onChange={(checked) => handleItemChange(index, 'performance_related', checked)}
          size="small"
        />
      ),
    },
    {
      title: () => (
        <span>
          目标业绩 
          <Tooltip title="目标业绩金额">
            <InfoCircleOutlined style={{ marginLeft: 4, color: '#999' }} />
          </Tooltip>
        </span>
      ),
      key: 'target_amount',
      width: 120,
      render: (_, record, index) => (
        <InputNumber
          value={parseFloat(record.target_amount) || 0}
          onChange={(value) => handleItemChange(index, 'target_amount', value || 0)}
          placeholder="0"
          min={0}
          precision={2}
          style={{ width: '100%' }}
          formatter={value => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={value => value.replace(/¥\s?|(,*)/g, '')}
        />
      ),
    },
    {
      title: () => (
        <span>
          计算周期 
          <Tooltip title="业绩计算周期">
            <InfoCircleOutlined style={{ marginLeft: 4, color: '#999' }} />
          </Tooltip>
        </span>
      ),
      key: 'cycle_type',
      width: 100,
      render: (_, record, index) => (
        <Select
          value={record.cycle_type}
          onChange={(value) => handleItemChange(index, 'cycle_type', value)}
          style={{ width: '100%' }}
          size="small"
        >
          <Option value="monthly">月度</Option>
          <Option value="quarterly">季度</Option>
          <Option value="yearly">年度</Option>
        </Select>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (_, record, index) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveItem(index)}
          size="small"
        />
      ),
    },
  ];

  return (
    <Modal
      title={initialData ? '编辑薪资模板' : '新建薪资模板'}
      open={visible}
      onCancel={onCancel}
      width={1000}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          取消
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
          {initialData ? '更新' : '创建'}
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          is_active: true,
          effective_date: new Date().toISOString().split('T')[0]
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
          <Form.Item
            name="position_id"
            label="适用岗位"
            rules={[{ required: true, message: '请选择适用岗位' }]}
          >
            <Select placeholder="选择岗位" showSearch optionFilterProp="children">
              {positions.map(pos => (
                <Option key={pos.id} value={pos.id}>
                  {pos.department_name} - {pos.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="name"
            label="模板名称"
            rules={[{ required: true, message: '请输入模板名称' }]}
          >
            <Input placeholder="输入模板名称" />
          </Form.Item>

          <Form.Item
            name="effective_date"
            label="生效日期"
            rules={[{ required: true, message: '请选择生效日期' }]}
          >
            <Input type="date" />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="是否启用"
            valuePropName="checked"
          >
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>
        </div>

        <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '6px' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
            <InfoCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
            <strong style={{ color: '#52c41a' }}>重要提示</strong>
          </div>
          <div style={{ color: '#666', fontSize: '13px', lineHeight: '1.5' }}>
            <p style={{ margin: '0 0 4px 0' }}>• 每个岗位只能设置一个有效薪资模板</p>
            <p style={{ margin: '0 0 4px 0' }}>• 创建新模板时，该岗位的旧模板将自动设为无效</p>
            <p style={{ margin: '0' }}>• 员工入职后将自动根据岗位薪资模板生成薪资配置</p>
          </div>
        </div>

        <Form.Item name="description" label="模板描述">
          <TextArea rows={3} placeholder="输入模板描述（可选）" />
        </Form.Item>
      </Form>

      <div style={{ marginTop: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h4 style={{ margin: 0 }}>薪资项目配置</h4>
          <Button type="dashed" icon={<PlusOutlined />} onClick={handleAddItem}>
            添加薪资项目
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={templateItems}
          pagination={false}
          size="small"
          rowKey={(record, index) => record.id || index}
          locale={{ emptyText: '暂无薪资项目，请点击"添加薪资项目"按钮添加' }}
          scroll={{ y: 300 }}
        />
      </div>
    </Modal>
  );
};

export default SalaryTemplateForm;
